import { motion, useReducedMotion } from "framer-motion";
import { Download, Copy, Check } from "lucide-react";
import { useState } from "react";
import { Section } from "./Section";
import { LucideIcon } from "@/components/marketing/LucideIcon";
import { MEDIA_KIT } from "@/content/landing";

export function MediaKit() {
  const reduce = useReducedMotion();
  const [copied, setCopied] = useState(false);

  const copyBoilerplate = async () => {
    try {
      await navigator.clipboard.writeText(MEDIA_KIT.boilerplate);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* no-op */ }
  };

  return (
    <Section
      id="media-kit"
      testId="landing-media-kit"
      eyebrow={MEDIA_KIT.eyebrow}
      title={MEDIA_KIT.title}
      subtitle={MEDIA_KIT.subtitle}
    >
      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        {MEDIA_KIT.items.map((item, i) => {
          const isFeatured = item.kind === "zip" || item.kind === "primary";
          return (
            <motion.a
              key={item.label}
              href={item.href}
              download
              data-testid={`media-kit-item-${i}`}
              initial={reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.45, ease: "easeOut", delay: (i % 3) * 0.08 }}
              className={`group flex items-center gap-4 rounded-2xl border p-6 backdrop-blur transition-all hover:-translate-y-0.5 ${
                isFeatured
                  ? "border-sema-mid/40 bg-gradient-to-br from-sema-primary/10 to-sema-mid/5"
                  : "border-border/60 bg-card/50 hover:border-sema-mid/40"
              }`}
            >
              <span
                className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl"
                style={{ backgroundColor: "rgba(91,120,229,0.14)", color: "#5B78E5" }}
              >
                <LucideIcon name={item.icon} className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="font-display text-base font-semibold tracking-tight text-foreground">{item.label}</div>
                <div className="font-body text-xs text-muted-foreground">{item.desc}</div>
              </div>
              <Download className="h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform group-hover:translate-y-0.5 group-hover:text-sema-mid" />
            </motion.a>
          );
        })}
      </div>

      {/* Boilerplate + press contact */}
      <div className="mt-8 rounded-2xl border border-border/60 bg-card/40 p-6 backdrop-blur sm:p-8">
        <div className="flex items-center justify-between gap-4">
          <h3 className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Company boilerplate</h3>
          <button
            type="button"
            onClick={copyBoilerplate}
            data-testid="media-kit-copy-boilerplate"
            className="inline-flex items-center gap-1.5 rounded-full border border-border/60 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground transition-colors hover:text-foreground"
          >
            {copied ? <Check className="h-3 w-3 text-sema-accent" /> : <Copy className="h-3 w-3" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <p className="mt-3 font-body text-sm leading-relaxed text-foreground/85">{MEDIA_KIT.boilerplate}</p>
        <p className="mt-5 font-body text-sm text-muted-foreground">
          Press enquiries:{" "}
          <a href={`mailto:${MEDIA_KIT.pressEmail}`} data-testid="media-kit-press-email" className="font-mono text-sema-mid hover:underline">
            {MEDIA_KIT.pressEmail}
          </a>
        </p>
      </div>
    </Section>
  );
}
